import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orderService } from '../services/order.service';
import { Card, CardContent, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/Button';
import { Loader2, ArrowLeft, MapPin, CreditCard } from 'lucide-react';

const statusColors: Record<string, string> = {
  PENDING: 'bg-yellow-500/10 text-yellow-500',
  PAID: 'bg-blue-500/10 text-blue-500',
  PROCESSING: 'bg-purple-500/10 text-purple-500',
  SHIPPED: 'bg-orange-500/10 text-orange-500',
  DELIVERED: 'bg-green-500/10 text-green-500',
  CANCELLED: 'bg-red-500/10 text-red-500',
  REFUNDED: 'bg-gray-500/10 text-gray-500',
};

export const OrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const { data: order, isLoading } = useQuery({
    queryKey: ['order', id],
    queryFn: () => orderService.getOrder(parseInt(id!)),
    enabled: !!id,
  });

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Retry a pending payment
  const retryPaymentMutation = useMutation({
    mutationFn: (orderId: number) => orderService.initializePayment(orderId),
    onSuccess: (data) => {
      if (data.paymentUrl) {
        window.location.href = data.paymentUrl;
      } else {
        navigate(`/checkout/pay/${data.orderId}?reference=${data.reference}`);
      }
    },
  });

  // Cancel a pending/paid order
  const cancelOrderMutation = useMutation({
    mutationFn: (orderId: number) => orderService.cancelOrder(orderId),
    onSuccess: () => {
      if (order?.id) {
        queryClient.invalidateQueries({ queryKey: ['order', String(order.id)] });
        queryClient.invalidateQueries({ queryKey: ['orders'] });
      }
    },
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold text-[rgb(var(--foreground))]">Order not found</h2>
        <Link to="/orders" className="mt-4 inline-block text-primary hover:underline">
          Back to orders
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to="/orders" className="text-sm text-primary hover:underline inline-flex items-center">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to Orders
          </Link>
          <h1 className="text-2xl font-bold text-[rgb(var(--foreground))]">
            Order #{order.orderNumber}
          </h1>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[order.status] || 'bg-gray-500/10 text-gray-500'}`}>
          {order.status}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Order Items */}
        <Card className="lg:col-span-2">
          <CardTitle>Items</CardTitle>
          <CardContent className="space-y-3">
            {order.items?.map((item) => (
              <div key={item.id} className="flex justify-between items-center border-b border-[rgb(var(--border))] pb-3 last:border-0 last:pb-0">
                <div>
                  <p className="font-medium">{item.productName}</p>
                  <p className="text-sm text-[rgb(var(--light-secondary))]">Qty: {item.quantity}</p>
                </div>
                <span className="font-semibold">GHS {(item.total / 100).toFixed(2)}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Order Summary */}
        <Card>
          <CardTitle>Order Summary</CardTitle>
          <CardContent className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-[rgb(var(--light-secondary))]">Subtotal</span>
              <span>GHS {(order.subtotal / 100).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[rgb(var(--light-secondary))]">Shipping</span>
              <span>{order.shippingCost === 0 ? 'Free' : `GHS ${(order.shippingCost / 100).toFixed(2)}`}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[rgb(var(--light-secondary))]">Tax</span>
              <span>GHS {(order.tax / 100).toFixed(2)}</span>
            </div>
            <div className="border-t border-[rgb(var(--border))] pt-3">
              <div className="flex justify-between font-bold">
                <span>Total</span>
                <span className="text-primary">GHS {(order.total / 100).toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Shipping Information */}
        {order.shippingAddress && (
          <Card>
            <CardTitle>
              <div className="flex items-center space-x-2">
                <MapPin className="h-5 w-5 text-primary" />
                <span>Shipping Address</span>
              </div>
            </CardTitle>
            <CardContent className="space-y-1 text-sm">
              <p>{order.shippingAddress.name}</p>
              <p>{order.shippingAddress.street}</p>
              <p>{order.shippingAddress.city}, {order.shippingAddress.region}</p>
              <p>{order.shippingAddress.phone}</p>
              {order.shippingMethod && (
                <p className="mt-2 font-medium">Method: {order.shippingMethod}</p>
              )}
              {order.trackingNumber && (
                <p>Tracking: {order.trackingNumber}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Payment Info */}
        <Card>
          <CardTitle>
            <div className="flex items-center space-x-2">
              <CreditCard className="h-5 w-5 text-primary" />
              <span>Payment Information</span>
            </div>
          </CardTitle>
          <CardContent className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-[rgb(var(--light-secondary))]">Method</span>
              <span>{order.paymentMethod || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[rgb(var(--light-secondary))]">Status</span>
              <span>{order.status === 'PAID' ? 'Paid' : 'Pending'}</span>
            </div>
            {order.paidAt && (
              <div className="flex justify-between">
                <span className="text-[rgb(var(--light-secondary))]">Paid at</span>
                <span>{new Date(order.paidAt).toLocaleString()}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        {order.status === 'PENDING' && (
          <Card>
            <CardContent className="p-4">
              <Button
                variant="primary"
                className="w-full"
                disabled={retryPaymentMutation.isPending}
                onClick={() => retryPaymentMutation.mutate(order.id)}
              >
                {retryPaymentMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CreditCard className="mr-2 h-4 w-4" />
                )}
                Retry Payment
              </Button>
              <Button
                variant="ghost"
                className="w-full mt-2 text-red-500 hover:text-red-600"
                disabled={cancelOrderMutation.isPending}
                onClick={() => cancelOrderMutation.mutate(order.id)}
              >
                Cancel Order
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};